/**
 * This file contains all global enums and types/interfaces used across the application
 *
 * This allows to easily change values later on without needing to change everything file by file
 */

export enum SortOrder {
    ASC = 'asc',
    DESC = 'desc',
}

export enum ViewType {
    GRID = 'grid',
    LIST = 'list',
}
/**
 * List of filter params used for filtering contents
 */
export enum FilterParams {
    CREATED_AT = 'createdAt',
    SORT_BY = 'sortBy',
    SORT_ORDER = 'sortOrder',
}

export enum DeleteType {
    SINGLE = 'single',
    BULK = 'bulk',
}

/** Common actions */
export enum Actions {
    DELETE = 'delete',
    PUSH_UPDATES = 'push updates',
    EDIT = 'edit',
    VIEW = 'view',
    DOWNLOAD = 'download',
}

/**
 * Represents the available client type codes, which define the role of a client in the system.
 */
export enum ClientTypeCode {
    /** A dealer client. */
    DEALER = 'dealer',
    /** An advertiser client. */
    ADVERTISER = 'advertiser',
    /** A sub-dealer client, typically under a dealer. */
    SUB_DEALER = 'sub_dealer',
    /** A dealer with administrative privileges. */
    DEALER_ADMIN = 'dealer_admin',
    /** A super administrator with the highest level of access. */
    SUPER_ADMIN = 'super_admin',
    /** A standard user client. */
    USER = 'user',
    /** A host client. */
    HOST = 'host',
}

/**
 * Represents the available client type names, which define the role of a client in the system.
 */
export enum ClientTypeName {
    DEALER = 'Dealer',
    SUPER_ADMIN = 'Super Admin',
    USER = 'User',
    HOST = 'Host',
    ADVERTISER = 'Advertiser',
    SUB_DEALER = 'Sub Dealer',
    DEALER_ADMIN = 'Dealer Admin',
}

/** Table cell click generic payload */
export interface TableCellClickPayload<TValue = any, TRow = any> {
    /** The column field key */
    field: string;
    /** The cell value */
    value: TValue;
    /** The row data */
    row: TRow;
}

/**
 * Table state payload for filtering, pagination, and sorting.
 * When `filter: true` is set on a table column configuration/constant
 *
 * @property filters - Column filters as key-value pairs.
 * @property page - Current page index.
 * @property pageSize - Number of rows per page.
 * @property sortField - Field used for sorting.
 * @property sortOrder - Sort direction.
 */
export interface TableFilter {
    filters: Record<string, string>;
    page: number;
    pageSize: number;
    sortField: string;
    sortOrder: SortOrder;
}

/**
 * User account status
 */
export enum AcountStatus {
    ACTIVE = 1,
    INACTIVE = 0,
}

/**
 * Default avatar user profile s3 link.
 * This will be set as the default profileUrl if no upload was initiated to prevent duplicate uploads on filestack/s3 for the same default
 * avatar image
 */
export const DEFAULT_USER_AVATAR =
    'https://n-compass-filestack.s3.us-east-2.amazonaws.com/default_profile.png';
